/**
 * Route guard for the treatment-catalog browse page
 * (CLINIC-CATALOG-PRICING-TRUTH-S1-V1). Mirrors RequireClinicalWorkbench:
 * the pure decision lives in decideTreatmentCatalogAccess
 * (catalog-browse-view.ts) and keys off the EXISTING catalog audience --
 * doctor + owner / gm_agm / regional_head (the frozen backend
 * TREATMENT_CATALOG_READ_ROLES mirror; visibility-doctrine rule 6: an
 * implementation of the existing audience, never an ad-hoc list). Any
 * other role redirects to its own homePath. Role-only gate: NO /me flag
 * signal, NO feature flag, NO new visibility mechanism (internal
 * master-data surface, default-on per ADR-0016; the S1 ruling).
 *
 * Backend route auth (GET /api/treatment-catalog role gate BEFORE DB +
 * the org/clinic fences) remains the real security boundary; this guard
 * is UX cleanliness only.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { decideTreatmentCatalogAccess } from '@/dashboard/catalog/catalog-browse-view';
import type { DashboardMe } from '@/dashboard/types';

export interface RequireTreatmentCatalogViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  /** Rendered when access is granted. Defaults to `<Outlet />`. */
  readonly children?: React.ReactNode;
}

/** Pure presentational view - testable via react-dom/server. */
export function RequireTreatmentCatalogView({
  loading,
  error,
  me,
  children,
}: RequireTreatmentCatalogViewProps): JSX.Element | null {
  const decision = decideTreatmentCatalogAccess({ loading, error, me });
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

export default function RequireTreatmentCatalog(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequireTreatmentCatalogView
      loading={ctx.loading}
      error={ctx.error}
      me={ctx.me}
    />
  );
}
